import { Link } from "react-router-dom";
import {
  PackageOpen,
  Send,
  LineChart,
  ArrowRight,
  CheckCircle2,
  FileText,
  Globe,
  Truck,
  Scale,
} from "lucide-react";
import { useReveal } from "../lib/reveal";

const exportImg =
  "https://images.unsplash.com/photo-1613690399151-65ea69478674?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwyfHxjYXJnbyUyMHNoaXAlMjBvY2VhbiUyMGFlcmlhbHxlbnwwfHx8fDE3NzkwODQyMjF8MA&ixlib=rb-4.1.0&q=85";

const importImg =
  "https://images.unsplash.com/photo-1678182451047-196f22a4143e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwzfHxzaGlwcGluZyUyMGNvbnRhaW5lcnMlMjBwb3J0fGVufDB8fHx8MTc3OTA4NDIyMXww&ixlib=rb-4.1.0&q=85";

const consultImg =
  "https://images.unsplash.com/photo-1638262052640-82e94d64664a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMGhhbmRzaGFrZXxlbnwwfHx8fDE3NzkwODQyMjF8MA&ixlib=rb-4.1.0&q=85";

const services = [
  {
    id: "import",
    icon: PackageOpen,
    title: "Import Services",
    tag: "01 · Sourcing",
    img: importImg,
    features: [
      "Sourcing products from trusted global suppliers",
      "Handling customs clearance, documentation, and logistics",
      "Quality verification & supplier audits",
    ],
  },
  {
    id: "export",
    icon: Send,
    title: "Export Services",
    tag: "02 · Distribution",
    img: exportImg,
    features: [
      "Connecting your products to international markets",
      "Managing compliance, shipping, and distribution efficiently",
      "Multi-modal logistics: sea, air and land",
    ],
  },
  {
    id: "consulting",
    icon: LineChart,
    title: "Consulting & Support",
    tag: "03 · Strategy",
    img: consultImg,
    features: [
      "Trade consulting to optimize costs and logistics",
      "Guidance on international trade regulations",
      "Market entry & route optimization advisory",
    ],
  },
];

const processSteps = [
  { icon: FileText, title: "Inquiry & Quote", desc: "Share requirements and we deliver a transparent quote." },
  { icon: Scale, title: "Compliance & Docs", desc: "All certifications and customs paperwork handled." },
  { icon: Truck, title: "Logistics", desc: "Multi-modal routing across our partner network." },
  { icon: Globe, title: "Delivery", desc: "Goods delivered to the destination on time, every time." },
];

export default function Services() {
  const ref = useReveal();

  return (
    <div ref={ref} data-testid="services-page">
      {/* HEADER */}
      <section className="relative bg-[#0E2A3F] text-white overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.9) 1px, transparent 0)",
            backgroundSize: "32px 32px",
          }}
        />
        <div className="absolute -top-40 -left-40 h-96 w-96 rounded-full bg-[#F26F21]/15 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <span className="reveal text-[11px] uppercase tracking-[0.24em] text-[#F26F21] font-semibold">
            What We Do
          </span>
          <h1 className="reveal mt-5 font-[Outfit] text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight max-w-3xl leading-tight">
            Our Services.
          </h1>
          <p className="reveal mt-7 max-w-2xl text-slate-300 text-base lg:text-lg leading-relaxed">
            At VRVR Import and Exports, we specialize in moving goods, ideas and businesses
            across borders — with a service portfolio engineered for clarity and scale.
          </p>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-24 lg:py-32 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16 lg:space-y-24">
          {services.map((s, i) => (
            <div
              key={s.id}
              id={s.id}
              data-testid={`service-${s.id}`}
              className={`grid lg:grid-cols-12 gap-10 lg:gap-16 items-center ${
                i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""
              }`}
            >
              <div className="lg:col-span-6 reveal">
                <div className="relative">
                  <div
                    className={`absolute -inset-3 rounded-2xl ${
                      i % 2 === 0 ? "border-2 border-[#F26F21]/30 -rotate-1" : "bg-[#0E2A3F]/8 rotate-1"
                    }`}
                  />
                  <img
                    src={s.img}
                    alt={s.title}
                    className="relative rounded-2xl object-cover w-full h-[420px] shadow-xl"
                  />
                  <div className="absolute top-5 left-5 rounded-full bg-white/95 backdrop-blur px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] font-semibold text-[#0E2A3F]">
                    {s.tag}
                  </div>
                </div>
              </div>

              <div className="lg:col-span-6 reveal">
                <span className="inline-flex h-14 w-14 items-center justify-center rounded-xl bg-[#0E2A3F] text-white shadow-lg">
                  <s.icon className="h-6 w-6" strokeWidth={1.7} />
                </span>
                <h2 className="mt-6 font-[Outfit] text-3xl lg:text-4xl font-bold text-[#0E2A3F] tracking-tight">
                  {s.title}
                </h2>
                <ul className="mt-7 space-y-4">
                  {s.features.map((f) => (
                    <li key={f} className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-[#F26F21] mt-0.5 shrink-0" />
                      <span className="text-[#0E2A3F] leading-relaxed">{f}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/contact"
                  data-testid={`service-cta-${s.id}`}
                  className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-[#F26F21] hover:gap-3 transition-all"
                >
                  Discuss this service <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl reveal">
            <span className="text-[11px] uppercase tracking-[0.24em] text-[#F26F21] font-semibold">
              How We Work
            </span>
            <h2 className="mt-4 font-[Outfit] text-3xl lg:text-4xl font-bold text-[#0E2A3F] tracking-tight">
              A simple four-step process.
            </h2>
            <p className="mt-4 text-[#475569]">
              From first inquiry to final delivery — clear milestones, zero surprises.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {processSteps.map((p, i) => (
              <div
                key={p.title}
                data-testid={`process-step-${i}`}
                className="reveal lift relative rounded-xl border border-slate-200 bg-[#F8FAFC] p-7"
              >
                <div className="text-[11px] font-mono text-[#F26F21] tracking-widest">
                  STEP {String(i + 1).padStart(2, "0")}
                </div>
                <span className="mt-4 inline-flex h-11 w-11 items-center justify-center rounded-lg bg-[#0E2A3F] text-white">
                  <p.icon className="h-5 w-5" strokeWidth={1.8} />
                </span>
                <h3 className="mt-5 font-[Outfit] text-lg font-semibold text-[#0E2A3F]">{p.title}</h3>
                <p className="mt-2 text-sm text-[#475569] leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="reveal relative overflow-hidden rounded-3xl bg-[#0E2A3F] p-10 lg:p-16">
            <div className="absolute -right-20 -bottom-20 h-72 w-72 rounded-full bg-[#F26F21]/25 blur-3xl" />
            <div className="relative grid md:grid-cols-2 gap-10 items-center">
              <div>
                <h3 className="font-[Outfit] text-3xl lg:text-4xl font-bold text-white tracking-tight">
                  Have a shipment in mind?
                </h3>
                <p className="mt-4 text-slate-300 max-w-md">
                  Send us the details and we&apos;ll respond with a transparent quote tailored
                  to your goods, route and timeline.
                </p>
              </div>
              <div className="md:justify-self-end">
                <Link
                  to="/contact"
                  data-testid="services-cta-quote"
                  className="inline-flex items-center gap-2 rounded-md bg-[#F26F21] px-7 py-4 text-sm font-semibold text-white hover:bg-[#D95B14] transition-colors"
                >
                  Contact Us for a Quote <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
